/**
 * Routing + the authentication gate.
 */

import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Shell } from './components/Shell';
import { useAuth, type Role } from './lib/auth';
import { Spinner } from './components/ui';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { PatientsPage, PatientDetailPage } from './pages/Patients';
import { PatientWorkspacePage } from './pages/PatientWorkspacePage';
import { NewPatientsPage } from './pages/NewPatients';
import { TreatmentPlansPage } from './pages/TreatmentPlans';
import { ClinicalTwinPage } from './pages/ClinicalTwin';
import { MessagesPage } from './pages/Messages';
import { MessagesInboxPage } from './pages/MessagesInbox';
import { LegalAuditTrailPage } from './pages/LegalAuditTrail';
import { ResourcesPage } from './pages/Resources';
import { LabsPage, LabReviewPage } from './pages/Labs';
import { ReportsPage, ReportDetailPage } from './pages/Reports';
import { AnalyticsPage } from './pages/Analytics';
import { SettingsPage } from './pages/Settings';
import { InBodyPage } from './pages/InBody';
import { ClinicalDashboardPage } from './pages/ClinicalDashboard';
import { PlatformProvidersPage } from './pages/PlatformProviders';
import { PlatformSubscriptionsPage } from './pages/PlatformSubscriptions';
import { PlatformPlanSettingsPage } from './pages/PlatformPlanSettings';
import { PlatformLegalPage } from './pages/PlatformLegal';
import { CredentialsPage } from './pages/Credentials';
import { PlanQuestionsPage } from './pages/PlanQuestions';
import { SignQueuePage } from './pages/SignQueuePage';
import { QuestionBankPage } from './pages/QuestionBank';
import { SignUpPage } from './pages/SignUp';
import { ApplicationPage } from './pages/Application';
import { BillingPage } from './pages/Billing';
import { RetainPage } from './pages/Retain';
import { RetainPatientPage } from './pages/RetainPatient';
import { PharmaciesPage } from './pages/Pharmacies';
import { PharmacyProductsPage } from './pages/PharmacyProducts';
import { PlatformPharmaciesPage } from './pages/PlatformPharmacies';
import {
  PlatformOverviewPage,
  PlatformClinicsPage,
  PlatformClinicDetailPage,
} from './pages/Platform';

export function App() {
  const { status, user } = useAuth();

  if (status === 'loading') {
    return (
      <div className="min-h-screen grid place-items-center">
        <span className="text-ink-muted">
          <Spinner className="h-6 w-6" />
        </span>
      </div>
    );
  }

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Q1, self-serve sign-up, reachable without a session. */}
        <Route path="/signup" element={<SignUpPage />} />
        {/* Everything else bounces to the login screen. */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      {/*
        * Signing up signs the new provider in, which swaps this route table
        * before the sign-up page can send them on. So a signed-in visit to
        * /signup goes where sign-up always meant to end: the application
        * page, whose last step is the Public listing.
        */}
      <Route path="/signup" element={<Navigate to="/application" replace />} />
      <Route element={<Shell />}>
        {/* V3, engagement alerts. Clinic-scoped, like every route below. */}
        <Route path="/retain" element={<RetainPage />} />
        {/* One patient's check-in history, alerts and actions (mockup 3). */}
        <Route path="/retain/:patientId" element={<RetainPatientPage />} />
        <Route
          index
          element={
            user?.role === 'SUPERADMIN' ? (
              <Navigate to="/platform" replace />
            ) : user?.role === 'MEDICAL_DIRECTOR' ? (
              // The medical director's home is the clinical dashboard, critical
              // values first, credentialing second.
              <Navigate to="/clinical" replace />
            ) : (
              <DashboardPage />
            )
          }
        />
        {/* A provider's own credentialing application and nurse CPA. */}
        <Route
          path="application"
          element={
            <RequireRole roles={['ADMIN', 'CLINICIAN', 'STAFF']}>
              <ApplicationPage />
            </RequireRole>
          }
        />
        {/* Q14, the provider's own card and their patients' cycles. */}
        <Route
          path="billing"
          element={
            <RequireRole roles={['ADMIN', 'CLINICIAN', 'STAFF']}>
              <BillingPage />
            </RequireRole>
          }
        />

        {/* Platform-owner (MCOS) billing panel, SUPERADMIN only, cross-clinic. */}
        <Route
          path="platform"
          element={
            <RequireRole roles={['SUPERADMIN']}>
              <PlatformOverviewPage />
            </RequireRole>
          }
        />
        {/* Dr. Joe's clinical dashboard, critical values and exceptions only. */}
        <Route
          path="clinical"
          element={
            <RequireRole roles={['MEDICAL_DIRECTOR']}>
              <ClinicalDashboardPage />
            </RequireRole>
          }
        />
        <Route
          path="platform/subscriptions"
          element={
            <RequireRole roles={['SUPERADMIN']}>
              <PlatformSubscriptionsPage />
            </RequireRole>
          }
        />
        <Route
          path="platform/legal"
          element={
            <RequireRole roles={['SUPERADMIN']}>
              <PlatformLegalPage />
            </RequireRole>
          }
        />
        {/* PH-002, PH-003: MCOS verifies each pharmacy and records its BAA. */}
        <Route
          path="platform/pharmacies"
          element={
            <RequireRole roles={['SUPERADMIN']}>
              <PlatformPharmaciesPage />
            </RequireRole>
          }
        />
        <Route
          path="platform/plan-settings"
          element={
            <RequireRole roles={['SUPERADMIN']}>
              <PlatformPlanSettingsPage />
            </RequireRole>
          }
        />
        <Route
          path="platform/providers"
          element={
            <RequireRole roles={['SUPERADMIN', 'MEDICAL_DIRECTOR']}>
              <PlatformProvidersPage />
            </RequireRole>
          }
        />
        <Route
          path="platform/clinics"
          element={
            <RequireRole roles={['SUPERADMIN']}>
              <PlatformClinicsPage />
            </RequireRole>
          }
        />
        <Route
          path="platform/clinics/:id"
          element={
            <RequireRole roles={['SUPERADMIN']}>
              <PlatformClinicDetailPage />
            </RequireRole>
          }
        />
        <Route path="patients" element={<PatientsPage />} />
        <Route path="patients/:id" element={<PatientDetailPage />} />
        <Route path="patients/:id/inbody" element={<InBodyPage />} />
        {/* Retired: the treatment plan lives in the dashboard patient panel. Old links land there. */}
        <Route path="patients/:id/treatment-plan" element={<TreatmentPlanRedirect />} />
        {/* The dashboard's patient panel for any patient, deep-linkable by tab. */}
        <Route path="patients/:id/workspace" element={<PatientWorkspacePage />} />
        <Route path="new-patients" element={<NewPatientsPage />} />
        {/* The four destinations the client's sidebar adds. All clinic-scoped,
            all backed by real data rather than a placeholder page. */}
        <Route path="treatment-plans" element={<TreatmentPlansPage />} />
        <Route
          path="clinical-twin"
          element={
            <RequireRole roles={['ADMIN', 'CLINICIAN']}>
              <ClinicalTwinPage />
            </RequireRole>
          }
        />
        {/* S-13: the inbox; the delivery log of what MCOS sent moved under it. */}
        <Route path="messages" element={<MessagesInboxPage />} />
        <Route path="messages/log" element={<MessagesPage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="labs" element={<LabsPage />} />
        <Route path="labs/:id" element={<LabReviewPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="reports/:id" element={<ReportDetailPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route
          path="sign-queue"
          element={
            <RequireRole roles={['MEDICAL_DIRECTOR']}>
              <SignQueuePage />
            </RequireRole>
          }
        />
        {/* S-21: Dr. Joe confirms new pharmacy products (client, 8 October). */}
        <Route
          path="pharmacy-products"
          element={
            <RequireRole roles={['MEDICAL_DIRECTOR']}>
              <PharmacyProductsPage />
            </RequireRole>
          }
        />
        <Route
          path="platform/question-bank"
          element={
            <RequireRole roles={['SUPERADMIN', 'MEDICAL_DIRECTOR']}>
              <QuestionBankPage />
            </RequireRole>
          }
        />
        <Route path="plan-questions" element={<Navigate to="/ready-to-prescribe" replace />} />
        {/* LA-007: the clinic's own legal audit trail. */}
        <Route
          path="settings/audit-trail"
          element={
            <RequireRole roles={['ADMIN', 'CLINICIAN']}>
              <LegalAuditTrailPage />
            </RequireRole>
          }
        />
        <Route
          path="ready-to-prescribe"
          element={
            <RequireRole roles={['ADMIN', 'CLINICIAN']}>
              <PlanQuestionsPage />
            </RequireRole>
          }
        />
        <Route
          path="settings/credentials"
          element={
            <RequireRole roles={['ADMIN', 'CLINICIAN', 'MEDICAL_DIRECTOR']}>
              <CredentialsPage />
            </RequireRole>
          }
        />
        {/* S-20: the clinic's pharmacies and their price lists. */}
        <Route
          path="settings/pharmacies"
          element={
            <RequireRole roles={['ADMIN', 'CLINICIAN']}>
              <PharmaciesPage />
            </RequireRole>
          }
        />
        <Route
          path="settings"
          element={
            <RequireRole roles={['ADMIN']}>
              <SettingsPage />
            </RequireRole>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Client-side role gate. The API enforces the same rule server-side (§12). */
/** The standalone treatment plan page is gone; its old address opens the patient panel. */
function TreatmentPlanRedirect() {
  const { id } = useParams();
  return <Navigate to={`/patients/${id ?? ''}/workspace`} replace />;
}

function RequireRole({ roles, children }: { roles: Role[]; children: React.ReactNode }) {
  const { can } = useAuth();
  if (!can(...roles)) {
    return (
      <div className="glass-standard rounded-md2 p-8 text-center">
        <p className="font-display text-sm font-semibold text-ink">You do not have access to this page</p>
        <p className="mt-1 text-sm text-ink-muted">
          Ask an administrator if you need it.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}
